import { IMessage, spLogTags } from '@eventr/common'
import {
    ColoursObj,
    drawerStore,
    Icon,
    MobileDrawerFrame,
    Typography,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { signal } from '@preact/signals-react'
import { motion } from 'framer-motion'
import { DateTime } from 'luxon'
import { useCallback } from 'react'
import { useLongPress } from 'use-long-press'
import { MemberProfilePic } from '../../../home/team/MemberProfilePic'
import i18n from '../../../i18n/i18next'
import { useLanguageContext } from '../../../state/languageState/languageContext'
import { openMobileMapsLink } from '../../../utils'
import { isConnectivityAvailable } from '../../connectivity/connectivityListener'
import { useRocketChatContext } from '../rocketChatProvider'
import {
    emojiPickerState,
    MessageActionSheetBody,
} from './actionSheet/messageActionSheetBody'
import { useChatBodyContext } from './chatProvider'
import Markup from './MarkdownRenderer/Markup'
import { DownloadTypeMessage } from './message/downloadTypeMessage'
import { ImageTypeMessage } from './message/imageTypeMessage'
import { MessageReactions } from './message/messageReactions'

export type ChatMessageType = {
    message: IMessage
    lastMessage: IMessage | undefined
    lastMessageUserId?: string
}

export const DOWNLOAD_TYPES = [
    'application/pdf',
    'text/plain',
    'application/json',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-excel',
    'application/msword',
    'application/vnd.ms-powerpoint',
]

export const actionSheetOpen = signal(false)

export const ChatMessage: React.FC<ChatMessageType> = ({
    message,
    lastMessage,
}) => {
    const { getDownloadUrl } = useChatBodyContext()
    const { rcUserInfo, rcUserList } = useRocketChatContext()

    const bind = useLongPress(
        () => {
            if (isConnectivityAvailable.value) {
                drawerStore().openDrawer({
                    node: (
                        <MobileDrawerFrame
                            title="Options"
                            minHeight="7vh"
                            withCloseButton
                        >
                            <MessageActionSheetBody
                                message={message}
                                getDownloadUrl={getDownloadUrl}
                                rcUserInfo={rcUserInfo}
                            />
                        </MobileDrawerFrame>
                    ),
                    onBackdropClick: () => {
                        if (actionSheetOpen.value) {
                            drawerStore().closeDrawer()
                        }
                    },
                    onOpen: () => {
                        actionSheetOpen.value = true
                    },
                    onClose: () => {
                        actionSheetOpen.value = false
                        emojiPickerState.value = false
                    },
                })
            }
        },
        {
            threshold: 500, // In milliseconds SET TO 500 FOR A REASON: if any lower seems to cause the onclick of the stuff inside the drawer to trigger. so please test properly if you go lower.
        }
    )
    const now = new Date()
    const lastMessageDate = lastMessage ? new Date(lastMessage.ts) : undefined
    const messageDate = new Date(message.ts)
    const difference = lastMessage
        ? messageDate.getTime() - lastMessageDate.getTime()
        : undefined

    const isDownloadType = () => {
        return message.file && DOWNLOAD_TYPES.includes(message.file.type)
    }

    function getTimeBreak() {
        if (difference > 300000) {
            if (messageDate.getDate() === now.getDate()) {
                return (
                    <Typography
                        variant="bodySubtext"
                        justifyContent={'center'}
                        display={'flex'}
                        paddingTop={'8px'}
                        color={ColoursObj.core.primary}
                    >
                        {DateTime.fromJSDate(messageDate).toFormat(
                            useLanguageContext.getState().timeFormat,
                            {
                                locale: useLanguageContext
                                    .getState()
                                    .getLanguageISO639Code(),
                            }
                        )}
                    </Typography>
                )
            } else if (difference > 900000) {
                return (
                    <Typography
                        variant="bodySubtext"
                        justifyContent={'center'}
                        display={'flex'}
                        paddingTop={'8px'}
                        color={ColoursObj.core.primary}
                    >
                        {DateTime.fromJSDate(messageDate).toFormat(
                            'EEE, MMM d ' +
                                useLanguageContext.getState().timeFormat,
                            {
                                locale: useLanguageContext
                                    .getState()
                                    .getLanguageISO639Code(),
                            }
                        )}
                    </Typography>
                )
            }
        } else {
            return <div />
        }
    }

    function checkMessageTypes() {
        if (message.t) {
            switch (message.t) {
                case 'r':
                    spLogTags('Missing case for message type: ' + message.t, [
                        'rocketchat',
                        'ERROR',
                    ])
                    return
                case 'ul':
                    return (
                        <Typography
                            variant="bodySubtext"
                            color={ColoursObj.shades.shade3}
                            textAlign={'center'}
                            padding={'8px'}
                        >
                            {i18n.t('chat.core.user-left')}
                        </Typography>
                    )
                case 'ult':
                    spLogTags('Missing case for message type: ' + message.t, [
                        'rocketchat',
                        'ERROR',
                    ])
                    return
                case 'uj':
                    return (
                        <Typography
                            variant="bodySubtext"
                            // color={ColoursObj.font.secondary}
                            color={ColoursObj.shades.shade3}
                            textAlign={'center'}
                            padding={'8px'}
                        >
                            {i18n.t('chat.core.user-joined')}
                        </Typography>
                    )
                case 'ujt':
                    spLogTags('Missing case for message type: ' + message.t, [
                        'rocketchat',
                        'ERROR',
                    ])
                    return
                case 'ut':
                    spLogTags('Missing case for message type: ' + message.t, [
                        'rocketchat',
                        'ERROR',
                    ])
                    return
                case 'rm':
                    spLogTags('Missing case for message type: ' + message.t, [
                        'rocketchat',
                        'ERROR',
                    ])
                    return
                case 'added-user-to-team':
                    spLogTags('Missing case for message type: ' + message.t, [
                        'rocketchat',
                        'ERROR',
                    ])
                    return
                case 'removed-user-from-team':
                    spLogTags('Missing case for message type: ' + message.t, [
                        'rocketchat',
                        'ERROR',
                    ])
                    return
                case 'user-muted':
                    spLogTags('Missing case for message type: ' + message.t, [
                        'rocketchat',
                        'ERROR',
                    ])
                    return
                case 'au':
                    return (
                        <Typography
                            variant="bodySubtext"
                            // color={ColoursObj.font.secondary}
                            color={ColoursObj.shades.shade3}
                            textAlign={'center'}
                            padding={'8px'}
                        >
                            {i18n.t('rocketChat.userJoinedChannel')}
                        </Typography>
                    )
                case 'ru':
                    return (
                        <Typography
                            variant="bodySubtext"
                            color={ColoursObj.shades.shade3}
                            textAlign={'center'}
                            padding={'8px'}
                        >
                            {i18n.t('rocketChat.userLeftChannel')}
                        </Typography>
                    )
                default:
                    spLogTags('Missing case for message type: ' + message.t, [
                        'rocketchat',
                        'ERROR',
                    ])
            }
        } else if (message.blocks) {
            if (
                message.blocks[0] &&
                message.blocks[0].type &&
                message.blocks[0].type === 'Location'
            ) {
                return sharedLocation()
            }
        } else {
            return returnMessage()
        }
    }

    function sharedLocation() {
        return (
            <Stack
                padding={'8px 16px'}
                width={'100%'}
                boxSizing={'border-box'}
                flexDirection={'row'}
                justifyContent={
                    rcUserInfo &&
                    message.u &&
                    message.u._id &&
                    rcUserInfo?._id === message.u._id
                        ? 'flex-end'
                        : 'flex-start'
                }
            >
                {!lastMessage ||
                (lastMessage &&
                    lastMessage.u &&
                    lastMessage.u._id &&
                    message.u &&
                    message.u._id &&
                    lastMessage.u._id !== message.u._id) ||
                (difference &&
                    difference > 300000 &&
                    now.getDate() === messageDate.getDate()) ||
                (difference && difference > 900000) ? (
                    <Stack
                        height={'32px'}
                        width={
                            rcUserInfo && rcUserInfo?._id === message.u._id
                                ? '0px'
                                : '32px'
                        }
                        paddingRight={'8px'}
                    >
                        {rcUserInfo && rcUserInfo?._id !== message.u._id && (
                            <MemberProfilePic
                                sideLength={32}
                                rcUser={rcUserList?.find(
                                    (u) => u._id === message.u._id
                                )}
                            />
                        )}
                    </Stack>
                ) : (
                    <Stack minWidth={'32px'} paddingRight={'8px'} />
                )}
                <Stack
                    maxWidth={'250px'}
                    bgcolor={ColoursObj.backgrounds.secondary}
                    borderRadius={'8px'}
                    padding={'8px'}
                    display={'inline-block'}
                    textAlign={'right'}
                >
                    {rcUserInfo &&
                        rcUserInfo?._id !== message.u._id &&
                        (!lastMessage ||
                            (lastMessage &&
                                lastMessage.u._id !== message.u._id) ||
                            (difference &&
                                difference > 300000 &&
                                now.getDate() === messageDate.getDate()) ||
                            (difference && difference > 900000)) && (
                            <Typography
                                variant="bodyBold"
                                width={'fit-content'}
                                style={{ wordBreak: 'break-word' }}
                            >
                                {message.u
                                    ? message.u.name ??
                                      message.u.username ??
                                      message.u._id
                                    : 'User'}
                            </Typography>
                        )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <div
                            onClick={() => {
                                const coords = message.msg.split(',')
                                openMobileMapsLink(`${coords[0]},${coords[1]}`)
                            }}
                            style={{
                                border: '2px solid #007AFF',
                                borderRadius: '50px',
                                minWidth: '50px',
                                width: '50px',
                                height: '50px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <div
                                style={{
                                    border: '2px solid #007AFF',
                                    borderRadius: '50px',
                                    width: '35px',
                                    height: '35px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Icon name="Location" iconColour="#007AFF" />
                            </div>
                        </div>
                        <Typography textAlign={'left'}>
                            <span style={{ textAlign: 'left' }}>
                                {i18n.t('location.chat-shared')}
                            </span>
                            <span
                                style={{
                                    fontSize: '12px',
                                    lineHeight: '16px',
                                    fontWeight: '400',
                                    color: ColoursObj.font.primary,
                                    paddingLeft: '8px',
                                    paddingTop: '8px',
                                    float: 'right',
                                }}
                            >
                                {DateTime.fromJSDate(messageDate).toFormat(
                                    useLanguageContext.getState().timeFormat,
                                    {
                                        locale: useLanguageContext
                                            .getState()
                                            .getLanguageISO639Code(),
                                    }
                                )}
                            </span>
                        </Typography>
                    </div>
                </Stack>
            </Stack>
        )
    }

    function returnMessage() {
        return (
            <motion.div
                whileTap={{
                    backgroundColor: ColoursObj.shades.shade3,
                    scale: 0.9,
                }}
            >
                <Stack
                    {...bind()}
                    padding={'4px 16px'}
                    width={'100%'}
                    boxSizing={'border-box'}
                    flexDirection={'row'}
                    justifyContent={
                        rcUserInfo &&
                        message.u &&
                        message.u._id &&
                        rcUserInfo?._id === message.u._id
                            ? 'flex-end'
                            : 'flex-start'
                    }
                >
                    {!lastMessage ||
                    (lastMessage &&
                        lastMessage.u &&
                        lastMessage.u._id &&
                        message.u &&
                        message.u._id &&
                        lastMessage.u._id !== message.u._id) ||
                    (difference &&
                        difference > 300000 &&
                        now.getDate() === messageDate.getDate()) ||
                    (difference && difference > 900000) ? (
                        <Stack
                            height={'32px'}
                            width={
                                rcUserInfo && rcUserInfo?._id === message.u._id
                                    ? '0px'
                                    : '32px'
                            }
                            paddingRight={'8px'}
                        >
                            {rcUserInfo &&
                                rcUserInfo?._id !== message.u._id && (
                                    <MemberProfilePic
                                        sideLength={32}
                                        rcUser={rcUserList?.find(
                                            (u) => u._id === message.u._id
                                        )}
                                    />
                                )}
                        </Stack>
                    ) : (
                        <Stack minWidth={'32px'} paddingRight={'8px'} />
                    )}
                    <Stack display={'grid'} gap={'4px'}>
                        <Stack
                            maxWidth={'250px'}
                            bgcolor={
                                rcUserInfo?._id === message?.u?._id
                                    ? ColoursObj.messaging.send
                                    : ColoursObj.messaging.receive
                            }
                            borderRadius={'8px'}
                            padding={'8px'}
                            width={'fit-content'}
                        >
                            {rcUserInfo &&
                                rcUserInfo?._id !== message.u._id &&
                                (!lastMessage ||
                                    (lastMessage &&
                                        lastMessage.u._id !== message.u._id) ||
                                    (difference &&
                                        difference > 300000 &&
                                        now.getDate() ===
                                            messageDate.getDate()) ||
                                    (difference && difference > 900000)) && (
                                    <Typography
                                        variant="bodyBold"
                                        width={'fit-content'}
                                        style={{ wordBreak: 'break-word' }}
                                    >
                                        {message.u
                                            ? message.u.name ??
                                              message.u.username ??
                                              message.u._id
                                            : 'User'}
                                    </Typography>
                                )}
                            {message.file && (
                                <div>
                                    {(message.file.type as string).startsWith(
                                        'image/'
                                    ) ? (
                                        <ImageTypeMessage message={message} />
                                    ) : isDownloadType() ? (
                                        <DownloadTypeMessage
                                            message={message}
                                        />
                                    ) : (
                                        <Stack
                                            spacing={'8px'}
                                            direction={'row'}
                                            alignItems={'center'}
                                        >
                                            <Box
                                                display={'flex'}
                                                alignItems={'center'}
                                                justifyContent={'center'}
                                                width={'48px'}
                                                height={'48px'}
                                                borderRadius={'4px'}
                                                bgcolor={
                                                    ColoursObj.backgrounds
                                                        .primary
                                                }
                                            >
                                                <Icon
                                                    name="Document"
                                                    width="24px"
                                                    height="24px"
                                                />
                                            </Box>
                                            <Typography variant="bodyBold">
                                                {message.file.name ??
                                                    i18n.t(
                                                        'rocketChat.fileNotRecognised'
                                                    )}
                                            </Typography>
                                        </Stack>
                                    )}
                                </div>
                            )}
                            <Typography
                                variant="bodyRegular"
                                style={{ wordBreak: 'break-word' }}
                            >
                                {renderMarkdown()}
                            </Typography>
                        </Stack>
                        <MessageReactions message={message} />
                    </Stack>
                </Stack>
            </motion.div>
        )
    }

    const renderMarkdown = useCallback(() => {
        if (message) {
            return Markup(message)
        }
    }, [message])

    return (
        <Stack>
            {lastMessage ? (
                getTimeBreak()
            ) : (
                <Typography
                    variant="bodySubtext"
                    justifyContent={'center'}
                    display={'flex'}
                    paddingTop={'8px'}
                    color={ColoursObj.core.primary}
                >
                    {DateTime.fromJSDate(messageDate).toFormat(
                        'EEE, MMM d ' +
                            useLanguageContext.getState().timeFormat,
                        {
                            locale: useLanguageContext
                                .getState()
                                .getLanguageISO639Code(),
                        }
                    )}
                </Typography>
            )}
            {checkMessageTypes()}
        </Stack>
    )
}
